import React, { useMemo, useRef, useState } from "react";
import { Alert, LinearProgress } from "@mui/material";
import GenericDataTable from "./GenericDataTable";
import BulkActionsBar from "./BulkActionsBar";
import AddToSequenceDialog from "./PopupWindows/AddToSequenceDialog";
import RejectedStageDialog from "./job-details/RejectedStageDialog";
import { RECRUITING_STAGES } from "../utils/pipelineStages";
import { buildCandidateSequenceItem } from "../utils/sequenceItems";
import { movePipelineCandidates, pipelineCandidateId } from "../utils/pipelineBulkActions";

export default function RecruitingPipelineTable({ enabled = true, scopeKey, ...props }) {
  return enabled ? <PipelineTable key={scopeKey} {...props} /> : <GenericDataTable {...props} />;
}

function PipelineTable({ request, apiBase, user, onMoved, loading, ...tableProps }) {
  const [selected, setSelected] = useState(new Map());
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [message, setMessage] = useState(null);
  const [sequenceOpen, setSequenceOpen] = useState(false);
  const [rejectionOpen, setRejectionOpen] = useState(false);
  const selectedIds = useMemo(() => [...selected.keys()], [selected]);
  const sequenceItems = useMemo(() => [...selected.values()].map(buildCandidateSequenceItem), [selected]);
  const rows = tableProps.rows || [];
  const changeSelection = ids => {
    if (busyRef.current) return;
    const available = new Map([...selected, ...rows.map(row => [pipelineCandidateId(row), row])]);
    setSelected(new Map(ids.map(String).filter(id => available.has(id)).map(id => [id, available.get(id)])));
  };
  const moveStage = async (stage, rejection) => {
    if (busyRef.current || !selected.size) return;
    if (stage === "Rejected" && !rejection) { setRejectionOpen(true); return; }
    busyRef.current = true;
    setBusy(true);
    try {
      const { updatedIds, failedIds } = await movePipelineCandidates(request, apiBase, [...selected.values()], stage, rejection);
      setSelected(new Map([...selected].filter(([id]) => failedIds.has(id))));
      setMessage({ severity: failedIds.size ? "warning" : "success", text:
        `Moved ${updatedIds.size} candidates to ${stage}.${failedIds.size ? ` ${failedIds.size} could not be moved and remain selected. Retry the action.` : ""}` });
      if (updatedIds.size) await onMoved?.();
      if (!failedIds.size) setRejectionOpen(false);
      else if (rejection) throw new Error("Some candidates could not be passed. They remain selected; please retry.");
    } catch (error) {
      setMessage({ severity: "error", text: error.message || "Could not refresh the pipeline." });
      if (rejection) throw error;
    } finally { busyRef.current = false; setBusy(false); }
  };
  const actions = <BulkActionsBar selectedCount={selected.size} actions={[
    { type: "button", label: "Add to Sequence", confirm: false, disabled: !selected.size || busy || loading, onClick: () => setSequenceOpen(true) },
    { type: "menu", label: "Move Stage", options: RECRUITING_STAGES.map(stage => stage.label), disabled: !selected.size || busy || loading, onSelect: moveStage },
  ]} />;
  return <>
    {(busy || loading) && <LinearProgress aria-label="Loading pipeline candidates" />}
    {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}
    <GenericDataTable {...tableProps} includeSelectionColumn preserveSelectionAcrossPages
      selectionDisabled={busy || loading}
      selectedIds={selectedIds} onSelectedIdsChange={changeSelection}
      bulkActionsTop={actions} bulkActionsBottom={actions} paginationLoading={loading || busy} />
    <AddToSequenceDialog open={sequenceOpen} onClose={() => setSequenceOpen(false)}
      entityType="candidate" items={sequenceItems} user={user}
      initialCampaignType="recruiting" initialScreen="recipients"
      onCreated={() => { setSelected(new Map()); setSequenceOpen(false); }} />
    <RejectedStageDialog jobSpecific open={rejectionOpen} loading={busy} candidateCount={selected.size}
      onClose={() => setRejectionOpen(false)} onSubmit={({ reason, note, disposition, decisionBy }) => moveStage("Rejected", { reason, notes: note, disposition, decisionBy })} />
  </>;
}
